import type { TooltipCoordinates, TooltipProps } from '@library/types/tooltip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceFn } from '@library/hooks/useDebounce';

export const getTooltipTriggerAttributes = (
  handleHover: (isOnHover: boolean) => void,
  showTooltip: boolean
) => ({
  role: 'button',
  tabIndex: 0,
  onMouseEnter: () => handleHover(true),
  onMouseLeave: () => handleHover(false),
  onKeyDown: (e: React.KeyboardEvent) =>
    ['Enter', ' '].includes(e.key) && handleHover(!showTooltip),
  onTouchStart: () => handleHover(true),
  onTouchEnd: () => handleHover(false)
});

export const useTooltip = ({
  position,
  text = ''
}: {
  position: TooltipProps['position'];
  text?: string | Array<string>;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipCoordinates>({
    top: 0,
    left: 0
  });

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(
    ({
      pos,
      rect,
      tooltip
    }: {
      pos: TooltipProps['position'];
      rect: DOMRect;
      tooltip: HTMLDivElement;
    }) => {
      const PADDING = 10;
      const tooltipHeight = tooltip.offsetHeight;
      const tooltipWidth = tooltip.offsetWidth;

      switch (pos) {
        case 'below':
          return {
            top: rect.bottom + PADDING,
            left: rect.left + rect.width / 2 - tooltipWidth / 2
          };
        case 'left':
          return {
            top: rect.top + rect.height / 2 - tooltipHeight / 2,
            left: rect.left - tooltipWidth - PADDING
          };
        case 'right':
          return {
            top: rect.top + rect.height / 2 - tooltipHeight / 2,
            left: rect.right + PADDING
          };
        default:
          return {
            top: rect.top - (tooltipHeight + PADDING),
            left: rect.left + rect.width / 2 - tooltipWidth / 2
          };
      }
    },
    []
  );

  const calcPositionByEvent = useCallback(() => {
    if (triggerRef.current && tooltipRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTooltipPosition(
        calculatePosition({
          pos: position,
          rect,
          tooltip: tooltipRef.current
        })
      );
    }
  }, [calculatePosition, position]);

  const handleHover = useCallback(
    (isOnHover: boolean) => {
      if (text.length === 0) {
        return;
      }

      setShowTooltip(isOnHover);

      if (isOnHover) {
        calcPositionByEvent();
      }
    },
    [calcPositionByEvent, text.length]
  );

  const triggerAttributes = useMemo(
    () => getTooltipTriggerAttributes(handleHover, showTooltip),
    [handleHover, showTooltip]
  );

  const { debounce: debouncedHandleResize } = useDebounceFn(
    calcPositionByEvent,
    500
  );

  useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [calcPositionByEvent, debouncedHandleResize]);

  return {
    showTooltip,
    tooltipPosition,
    tooltipRef,
    triggerAttributes,
    triggerRef
  };
};
